import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { usePromoteProspect, type Prospect } from "@/hooks/useProspects";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Mover para o funil": confirma nome, telefone, e-mail (opcional) e o resumo
 * que vira a mensagem do lead, chama `promote_prospect` (idempotente) e abre
 * o lead criado. Em lote (`prospects.length > 1`) usa os dados de cada linha
 * sem formulário.
 */
export interface PromoteProspectDialogProps {
  prospects: Prospect[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Depois de promover em lote (seleção limpa pelo pai). */
  onDone?: () => void;
}

export function PromoteProspectDialog({
  prospects,
  open,
  onOpenChange,
  onDone,
}: PromoteProspectDialogProps) {
  const navigate = useNavigate();
  const promote = usePromoteProspect();
  const single = prospects.length === 1 ? prospects[0] : null;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open || !single) return;
    setName(single.name);
    setPhone(single.phone ?? "");
    setEmail(single.email ?? "");
    setMessage(single.notes ?? "");
  }, [open, single]);

  const emailInvalid = email.trim() !== "" && !EMAIL_RE.test(email.trim());
  const pending = prospects.filter((p) => !p.lead_id);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (single) {
      if (!name.trim() || emailInvalid) return;
      const leadId = await promote.mutateAsync({
        id: single.id,
        overrides: {
          name: name.trim(),
          phone: phone.trim().slice(0, 20) || null,
          email: email.trim() || null,
          message: message.trim() || undefined,
        },
      });
      onOpenChange(false);
      navigate(`/admin/leads/${leadId}`);
      return;
    }
    setProgress(0);
    for (const p of pending) {
      await promote.mutateAsync({ id: p.id });
      setProgress((n) => n + 1);
    }
    onOpenChange(false);
    onDone?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Mover para o funil</DialogTitle>
            <DialogDescription>
              {single
                ? "Cria um lead em Novo no funil. O lead frio fica marcado como “No funil”."
                : `${pending.length} leads frios viram leads em Novo (os que já estão no funil são ignorados).`}
            </DialogDescription>
          </DialogHeader>

          {single && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="promote-name">Nome</Label>
                <Input
                  id="promote-name"
                  value={name}
                  maxLength={255}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="promote-phone">Telefone</Label>
                <Input
                  id="promote-phone"
                  value={phone}
                  maxLength={20}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="promote-email">E-mail (opcional)</Label>
                <Input
                  id="promote-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contato@empresa.com.br"
                  aria-invalid={emailInvalid}
                />
                {emailInvalid && (
                  <span className="text-xs font-semibold text-bad">E-mail inválido</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="promote-message">Resumo para o lead</Label>
                <Textarea
                  id="promote-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ex.: academia 10+ anos sem matrícula online — ofertar sistema + site"
                />
              </div>
            </div>
          )}

          {single && !email.trim() && (
            <p className="rounded-[10px] bg-warn-soft px-3 py-2.5 text-[12.5px] text-warn">
              Sem e-mail: proposta e contrato vão pedir um e-mail na hora de enviar.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                promote.isPending || (single ? !name.trim() || emailInvalid : pending.length === 0)
              }
            >
              {promote.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {single
                ? "Criar lead no funil"
                : promote.isPending
                  ? `Movendo ${progress}/${pending.length}…`
                  : `Mover ${pending.length}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
